import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../../utils/googleMaps';
import { MapPin, Clock, Truck, Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function LocalFlorists() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        
        if (mapRef.current && window.google) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: -33.8688, lng: 151.2093 }, // Sydney CBD coordinates
            zoom: 11,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });

          // Add marker for Sydney CBD
          new google.maps.Marker({
            position: { lat: -33.8688, lng: 151.2093 },
            map: map,
            title: "Sydney CBD",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "brand-primary",
              fillOpacity: 0.8,
              strokeColor: "#fff",
              strokeWeight: 2
            }
          });
        }
      } catch (error) {
        console.error('Error loading map:', error);
        setMapError(true);
      }
    };

    initMap();
  }, []);

  const localFloristsSearchUrl = "/search?location=Sydney+NSW%2C+Australia&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8688&lng=151.2093";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes local florists different from chain stores?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local florists offer personalized service, community knowledge, fresher flowers sourced directly from markets, and custom arrangements that reflect your neighborhood's character."
        }
      },
      {
        "@type": "Question",
        "name": "Do local florists offer same-day delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most local florists on MarketBloom offer same-day delivery within their service areas when orders are placed before 2pm on weekdays."
        }
      },
      {
        "@type": "Question",
        "name": "How do I find the best local florist near me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "MarketBloom connects you with verified local florists in your area. Look for florists with good reviews, community involvement, and those who source flowers frequently from wholesale markets."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Local Florists | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Find trusted local florists in your Sydney neighborhood. Support independent flower shops with personalized service, fresh blooms, and same-day delivery across Sydney." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Local Guides</span>
              <span>•</span>
              <span>15 January 2025</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Local Florists in Sydney for Same-Day Delivery 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/roses-hero14.jpg" 
              alt="Local florist arranging fresh flowers in Sydney neighborhood shop"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding authentic local florists in Sydney who deliver same-day shouldn't mean settling for overpriced corporate chains or generic supermarket bouquets.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent neighborhood flower shops - the passionate artisans from Paddington's boutiques to Leichhardt's family-run stores who understand their communities and craft arrangements that reflect each suburb's unique character.
            </p>
            <p className="leading-7 text-black">
              Whether you need urgent flower delivery to a Bondi apartment, a romantic surprise in Newtown, or birthday flowers for someone in Parramatta, our network of local florists ensures fresh, beautiful blooms arrive on time - often within hours of ordering, with the personal touch only neighborhood experts can provide.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Sydney's finest local florists offering same-day delivery options</p>
            <Link 
              to={`${localFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=local-florists`}
              className="bg-[#843329] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6b2820] transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Local Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Local Florists Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">What Makes Local Florists Special</h2>
            
            <p className="leading-7 text-black mb-6">
              Sydney's independent florists are as diverse as the neighborhoods they serve. From the established flower shops in Double Bay to the creative studios in Surry Hills, these local artisans understand what their communities love - whether it's native Australian flowers, sustainable practices, or designs that capture the essence of their suburb's personality.
            </p>
            <p className="leading-7 text-black mb-6">
              Unlike generic flower delivery services that ship from distant warehouses, local florists source from Sydney Flower Market daily, ensuring peak freshness. They know that a bouquet delivered to a Potts Point high-rise needs different handling than flowers for a Balmain terrace house, and arrangements for community events should reflect the neighborhood's unique character.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for personalized service" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Personalized Service</h3>
                <p className="text-black">Remember your preferences and special occasions</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for community knowledge" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Community Knowledge</h3>
                <p className="text-black">Know every street and local landmark in their area</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for fresh flowers" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Fresher Flowers</h3>
                <p className="text-black">Daily sourcing from Sydney's wholesale markets</p>
              </div>
            </div>
          </div>

          {/* Same-Day Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Delivery Coverage</h2>
            
            <p className="leading-7 text-black mb-6">
              Many of Sydney's best local florists offer same-day delivery with varying cutoff times throughout the day. This service covers neighborhoods across the metropolitan area, from the Eastern Suburbs to the Inner West.
            </p>

            <div className="mb-6">
              {mapError ? (
                <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">Map temporarily unavailable</p>
                </div>
              ) : (
                <div ref={mapRef} className="h-64 rounded-lg shadow-sm"></div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Link to="/blog/best-florists-bondi-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Bondi
              </Link>
              <Link to="/blog/best-florists-newtown-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Newtown
              </Link>
              <Link to="/blog/best-florists-surry-hills-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Surry Hills
              </Link>
              <Link to="/blog/best-florists-parramatta-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Parramatta
              </Link>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Paddington</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Leichhardt</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Balmain</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Marrickville</span>
            </div>
          </div>

          {/* Popular Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Popular Flower Styles Across Sydney Neighborhoods</h2>
            
            <p className="leading-7 text-black mb-6">
              Each Sydney neighborhood has its own floral preferences, and local florists understand these nuances perfectly. From minimalist arrangements in modern apartments to native Australian bouquets celebrating our unique landscape.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-black mb-4">Most Requested Styles:</h3>
              <ul className="space-y-2 text-black">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Native Australian arrangements with waratahs and eucalyptus
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Modern minimalist bouquets for contemporary homes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Vintage-inspired arrangements for heritage neighborhoods
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Seasonal mixed bouquets celebrating Sydney's climate
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Custom corporate arrangements for CBD offices
                </li>
              </ul>
            </div>
          </div>

          {/* Why MarketBloom Champions Local Florists */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Local Sydney Florists</h2>
            
            <p className="leading-7 text-black mb-6">
              We believe Sydney's neighborhoods deserve better than generic chain stores. That's why we exclusively partner with independent florists who are invested in their communities and passionate about their craft.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Community Investment</h3>
                  <p className="text-black">Local florists employ neighborhood residents, support local suppliers, and reinvest profits back into their communities.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Superior Quality</h3>
                  <p className="text-black">Daily sourcing from Sydney Flower Market ensures fresher blooms and longer-lasting arrangements than chain alternatives.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Personal Relationships</h3>
                  <p className="text-black">Build lasting connections with florists who remember your preferences and understand your needs.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Custom Creativity</h3>
                  <p className="text-black">Local florists can create unique, personalized arrangements that reflect your style and the recipient's personality.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">5</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Reliable Service</h3>
                  <p className="text-black">Local knowledge means better delivery routes, timing, and understanding of neighborhood access requirements.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Explore Other Areas */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Explore Florists in Other Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Each Sydney neighborhood has its own character, and our local florists reflect that diversity. Explore what makes each area special:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/blog/best-florists-bondi-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Bondi Florists</h3>
                <p className="text-sm text-gray-600">Coastal-inspired arrangements near the famous beach</p>
              </Link>
              <Link 
                to="/blog/best-florists-newtown-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Newtown Florists</h3>
                <p className="text-sm text-gray-600">Creative, bohemian designs in the Inner West</p>
              </Link>
              <Link 
                to="/blog/best-florists-surry-hills-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Surry Hills Florists</h3>
                <p className="text-sm text-gray-600">Artistic arrangements in Sydney's creative quarter</p>
              </Link>
              <Link 
                to="/blog/best-florists-parramatta-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Parramatta Florists</h3>
                <p className="text-sm text-gray-600">Professional service in Western Sydney's business hub</p>
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg p-8 mb-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <blockquote className="text-lg italic text-black mb-4">
                "I've been using MarketBloom to find local florists for over a year now, and the difference is incredible. My local florist in Paddington knows exactly what I like, remembers my anniversary, and always delivers the most beautiful, fresh arrangements. It's so much more personal than ordering from big chains!"
              </blockquote>
              <cite className="text-gray-600 font-semibold">
                — Rachel M., Paddington
              </cite>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqSchema.mainEntity.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0 pb-4 last:pb-0">
                  <button
                    className="w-full text-left flex justify-between items-center py-2 hover:text-brand-primary transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    aria-expanded={openFaq === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="font-semibold text-black pr-4">{faq.name}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div 
                      id={`faq-answer-${index}`}
                      className="mt-2 text-gray-600 leading-relaxed"
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                    >
                      {faq.acceptedAnswer.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-3">Ready to Support Your Local Florist?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Sydney's finest local florists and experience the difference</p>
            <Link 
              to={`${localFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=local-florists-bottom`}
              className="bg-[#843329] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6b2820] transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Local Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
} 